import { useEffect } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabase";

type TableName =
  | "projects"
  | "agents"
  | "runs"
  | "approvals"
  | "inbox"
  | "notes"
  | "dispatch_queue"
  | "heartbeats"
  | "project_tasks"
  | "project_notes"
  | "services"
  | "embedded_dashboards"
  | "events"
  | "priorities";

/**
 * Subscribe to all changes on a table and invalidate the React Query cache when
 * anything fires. Caller passes a callback (typically `() => qc.invalidateQueries(...)`).
 *
 * No-op when Supabase isn't configured (mock mode).
 */
export function useRealtimeTable(table: TableName, onChange: () => void) {
  useEffect(() => {
    if (!supabase) return;
    const client = supabase;
    // Unique channel name per hook instance — React Strict Mode runs effects
    // twice in dev. Reusing `realtime:<table>` would collide and trigger the
    // "cannot add postgres_changes callbacks after subscribe()" error.
    const channelName = `rt:${table}:${Math.random().toString(36).slice(2, 10)}`;
    const channel: RealtimeChannel = client
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table }, onChange)
      .subscribe();
    return () => {
      client.removeChannel(channel);
    };
  }, [table, onChange]);
}
